import moment from 'moment-timezone';
import type { Candle, SessionKey, SwingPoint } from './types.ts';

const TIME_ZONE = 'America/New_York';

export const computeEMA = (values: number[], period: number): Array<number | null> => {
  if (!values.length || period <= 0) return values.map(() => null);
  const k = 2 / (period + 1);
  const ema: Array<number | null> = values.map(() => null);
  if (values.length < period) return ema;
  const seed = values.slice(0, period).reduce((sum, val) => sum + val, 0) / period;
  ema[period - 1] = seed;
  for (let i = period; i < values.length; i += 1) {
    const prev = ema[i - 1] ?? values[i - 1];
    ema[i] = values[i] * k + prev * (1 - k);
  }
  return ema;
};

export const computeATR = (candles: Candle[], period: number): Array<number | null> => {
  if (!candles.length || period <= 0) return candles.map(() => null);
  const trs: number[] = candles.map((candle, idx) => {
    if (idx === 0) {
      return candle.h - candle.l;
    }
    const prevClose = candles[idx - 1].c;
    return Math.max(
      candle.h - candle.l,
      Math.abs(candle.h - prevClose),
      Math.abs(candle.l - prevClose)
    );
  });
  const atr: Array<number | null> = candles.map(() => null);
  if (trs.length < period) return atr;
  const seed = trs.slice(0, period).reduce((sum, val) => sum + val, 0) / period;
  atr[period - 1] = seed;
  for (let i = period; i < trs.length; i += 1) {
    const prev = atr[i - 1] ?? trs[i - 1];
    atr[i] = (prev * (period - 1) + trs[i]) / period;
  }
  return atr;
};

const getSessionBounds = (
  timeMs: number,
  session: SessionKey,
  customStart?: string,
  customEnd?: string
) => {
  const et = moment.tz(timeMs, TIME_ZONE);
  const dateStr = et.format('YYYY-MM-DD');
  let startTime = '00:00';
  let endTime = '23:59';

  if (session === 'LONDON') {
    startTime = '03:00';
    endTime = '11:00';
  } else if (session === 'NEW_YORK') {
    startTime = '08:00';
    endTime = '17:00';
  } else if (session === 'CUSTOM') {
    startTime = customStart || '00:00';
    endTime = customEnd || '23:59';
  }

  const start = moment.tz(`${dateStr} ${startTime}`, 'YYYY-MM-DD HH:mm', TIME_ZONE);
  let end = moment.tz(`${dateStr} ${endTime}`, 'YYYY-MM-DD HH:mm', TIME_ZONE);
  if (end.isBefore(start)) {
    end = end.add(1, 'day');
  }
  return { start, end, sessionId: start.format('YYYYMMDD') };
};

export const computeVWAP = (
  candles: Candle[],
  session: SessionKey,
  customStart?: string,
  customEnd?: string
) => {
  const vwap: Array<number | null> = candles.map(() => null);
  const hasVolume = candles.some((c) => typeof c.v === 'number' && c.v > 0);

  let cumulative = 0;
  let cumulativeVol = 0;
  let activeSessionId = '';

  candles.forEach((candle, idx) => {
    const { start, end, sessionId } = getSessionBounds(
      candle.t,
      session,
      customStart,
      customEnd
    );
    const time = moment.tz(candle.t, TIME_ZONE);
    if (!time.isBetween(start, end, undefined, '[]')) {
      return;
    }
    if (sessionId !== activeSessionId) {
      activeSessionId = sessionId;
      cumulative = 0;
      cumulativeVol = 0;
    }
    const typical = (candle.h + candle.l + candle.c) / 3;
    const weight = hasVolume ? candle.v ?? 0 : 1;
    cumulative += typical * weight;
    cumulativeVol += weight;
    if (cumulativeVol > 0) {
      vwap[idx] = cumulative / cumulativeVol;
    }
  });

  return {
    vwap,
    label: hasVolume ? 'VWAP' : 'VWAP (no volume)'
  };
};

export const computeSwings = (
  candles: Candle[],
  left = 2,
  right = 2
): SwingPoint[] => {
  const swings: SwingPoint[] = [];
  for (let i = left; i < candles.length - right; i += 1) {
    const high = candles[i].h;
    const low = candles[i].l;
    const isHigh = candles.slice(i - left, i + right + 1).every((c) => c.h <= high);
    const isLow = candles.slice(i - left, i + right + 1).every((c) => c.l >= low);
    if (isHigh) {
      swings.push({ index: i, price: high, type: 'HIGH' });
    }
    if (isLow) {
      swings.push({ index: i, price: low, type: 'LOW' });
    }
  }
  return swings;
};

export const computeTrendState = (swings: SwingPoint[]) => {
  const highs = swings.filter((s) => s.type === 'HIGH');
  const lows = swings.filter((s) => s.type === 'LOW');
  if (highs.length < 2 || lows.length < 2) return 'UNKNOWN';
  const lastHighs = highs.slice(-2);
  const lastLows = lows.slice(-2);
  const higherHighs = lastHighs[1].price > lastHighs[0].price;
  const higherLows = lastLows[1].price > lastLows[0].price;
  const lowerHighs = lastHighs[1].price < lastHighs[0].price;
  const lowerLows = lastLows[1].price < lastLows[0].price;
  if (higherHighs && higherLows) return 'UP';
  if (lowerHighs && lowerLows) return 'DOWN';
  return 'RANGE';
};

export const detectRange = (
  candles: Candle[],
  atr: Array<number | null>,
  window = 20,
  compression = 1.5
) => {
  if (candles.length < window) return null;
  const endIndex = candles.length - 1;
  const startIndex = Math.max(0, endIndex - window + 1);
  const slice = candles.slice(startIndex);
  const high = Math.max(...slice.map((c) => c.h));
  const low = Math.min(...slice.map((c) => c.l));
  const atrValue = atr[endIndex] ?? null;
  if (!atrValue) return null;
  const rangeSize = high - low;
  if (rangeSize > atrValue * compression) return null;
  return { startIndex, endIndex, high, low, isActive: true };
};

export const computeSessionBounds = getSessionBounds;
